import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Eye, EyeOff } from 'lucide-react';
import SEO from '../components/SEO';
import LocaleLink from '../components/LocaleLink';

const LOGO_URL = 'https://quidory.com/media/quidory-logo.png';

const LoginPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const lc = t.bind(null) && {
    headline: t('auth.login.headline'),
    sub: t('auth.login.subheadline'),
    emailLabel: t('auth.login.email'),
    emailPh: t('auth.login.emailPlaceholder'),
    pwLabel: t('auth.login.password'),
    pwPh: t('auth.login.passwordPlaceholder'),
    submit: t('auth.login.submit'),
    forgot: t('auth.login.forgot'),
    noAccount: t('auth.login.noAccount'),
    signup: t('auth.login.signup'),
    demo: t('auth.login.demo'),
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        titleEn="Sign In | Quidory"
        titleAr="تسجيل الدخول | كويدوري"
        descEn="Sign in to your Quidory account to access AI intelligence and publishing tools."
        descAr="سجّل الدخول إلى حساب كويدوري للوصول إلى أدوات الذكاء الاصطناعي والنشر."
        path="/login"
      />
      {/* Top bar */}
      <div className="p-6">
        <LocaleLink to="/" className="flex items-center gap-2 w-fit">
          <img src={LOGO_URL} alt="Quidory" className="h-7 w-7 object-contain" />
          <span className="font-semibold text-foreground text-sm">
            {language === 'ar' ? 'كويدوري' : 'Quidory'}
          </span>
        </LocaleLink>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-light tracking-tight text-foreground mb-2">{lc.headline}</h1>
            <p className="text-muted-foreground text-sm">{lc.sub}</p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
            <form data-testid="login-form" onSubmit={e => { e.preventDefault(); navigate('/dashboard'); }} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{lc.emailLabel}</label>
                <input
                  data-testid="login-email"
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder={lc.emailPh}
                  required
                  dir="ltr"
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-foreground">{lc.pwLabel}</label>
                  <button type="button" className="text-xs text-gold hover:text-gold/80 transition-colors">
                    {lc.forgot}
                  </button>
                </div>
                <div className="relative">
                  <input
                    data-testid="login-password"
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder={lc.pwPh}
                    required
                    dir="ltr"
                    className="w-full px-4 py-2.5 pe-11 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                data-testid="login-submit"
                type="submit"
                className="w-full py-3 rounded-lg bg-foreground text-background font-medium text-sm hover:bg-foreground/90 transition-colors"
              >
                {lc.submit}
              </button>
            </form>

            <div className="mt-4 pt-4 border-t border-border text-center">
              <button
                data-testid="demo-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="text-sm text-gold hover:text-gold/80 transition-colors font-medium"
              >
                {lc.demo} →
              </button>
            </div>

            <p className="mt-5 text-center text-sm text-muted-foreground">
              {lc.noAccount}{' '}
              <LocaleLink to="/signup" data-testid="goto-signup" className="text-foreground font-medium hover:text-gold transition-colors">
                {lc.signup}
              </LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
